import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useUploadFlow } from "../../hooks/useUploadFlow";
import { apiRoomNate } from "../../Slices/roomNateApis";
import { trackLayoutOpened } from "../../utils/analytics";
import UploadCard from "../UploadStates/UploadCard";
import UploadingCard from "../UploadStates/UploadingCard";
import ProcessingCard from "../UploadStates/ProcessingCard";
import DoneCard from "../UploadStates/DoneCard";
import ErrorCard from "../UploadStates/ErrorCard";
import RequirementsCard from "../UploadStates/RequirementsCard";
import { formatFileSize } from "../../utils/fileValidator";
import {
  CARD_MAX_WIDTH,
  CONTENT_BLOCK_HEIGHT,
} from "../UploadStates/uploadCardLayout";
import { normalizeGalleryUrlForCurrentOrigin } from "../../utils/galleryUrl";

const UNSUPPORTED_FILE_TYPE_COPY = "This file type isn't supported";

/**
 * Shared external-AI upload UI (drop zone + flow). Used on `/external-ai-upload`
 * and embedded on `/main` so both stay in sync.
 *
 * @param {'fullscreen'|'embedded'} layoutMode — fullscreen: centered full-viewport shell (embed page).
 *   embedded: bounded block for the user home page below the chrome.
 */
const UploadFlowPanel = ({ layoutMode, onUploadComplete }) => {
  const dispatch = useDispatch();
  const { isIntegrationMode, handleExit, ref } = useQueryParams();
  /** null = normal flow; 1–3 = requirement guide cards inside the fixed card slot */
  const [guideStep, setGuideStep] = useState(null);
  const guideUploadInputRef = useRef(null);

  // Preload requirement illustrations so guide navigation stays seamless.
  useEffect(() => {
    [
      "/assets/requirement-0.svg",
      "/assets/requirement-1.svg",
      "/assets/requirement-2.svg",
      "/assets/requirement-3.svg",
    ].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Integration contract (LOCKED — see MF_Integration_Guide.pdf):
  // partners embed `?ref=YOUR_LISTING_ID` and receive it echoed back on
  // `mf:complete`. When an iframe upload arrives with a `ref`, also forward
  // it to the backend as `customer_ref` so the lineage / listings rollup
  // happens automatically — no manual attach needed in Floor plans.
  // No-op for any non-integration upload path (ref is null).
  const integrationCustomerRef = isIntegrationMode && ref ? ref : null;

  const {
    state,
    selectedFile,
    previewUrl,
    validationError,
    uploadError,
    galleryUrl,
    uniqueId,
    processingStatus,
    elapsedTime,
    uploadProgress,
    handleFileSelect,
    handleCancel,
    handleTryAgain,
  } = useUploadFlow({ source: layoutMode, customerRef: integrationCustomerRef });

  const targetGalleryUrl = normalizeGalleryUrlForCurrentOrigin(galleryUrl);
  const uploadCompleteOnceKey = useRef("");
  const isUnsupportedValidation = validationError === UNSUPPORTED_FILE_TYPE_COPY;

  const openGuide = () => setGuideStep(1);

  const handleGuideUploadChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setGuideStep(null);
    handleTryAgain();
    handleFileSelect(file);
  };

  const handleGuideUploadAnother = () => {
    guideUploadInputRef.current?.click();
  };

  const handleErrorFileSelect = (file) => {
    // Reset error/analytics state first so a rejected replacement
    // lands on the idle UploadCard (validation message), and a valid
    // one goes straight into uploading — no extra "try again" hop.
    setGuideStep(null);
    handleTryAgain();
    handleFileSelect(file);
  };

  // Window-level OS file drop (fullscreen embed + /main dashboard). Zero visual change:
  // missing the card still uploads. Card handlers stopPropagation so in-card drops are
  // unchanged. Only claims drags whose dataTransfer includes Files and yields a real
  // File on drop — so in-app plan→folder moves (text/plain) are left alone. Gallery /
  // editor routes never mount this panel. Always preventDefault for OS file drags so
  // the browser does not navigate to the file during uploading/processing/done.
  const windowDropRef = useRef({
    state,
    guideStep,
    handleFileSelect,
    handleErrorFileSelect,
  });
  windowDropRef.current = {
    state,
    guideStep,
    handleFileSelect,
    handleErrorFileSelect,
  };

  useEffect(() => {
    const isOsFileDrag = (event) => {
      const types = event.dataTransfer?.types;
      if (!types) return false;
      return Array.from(types).includes("Files");
    };

    const onDragOver = (event) => {
      if (!isOsFileDrag(event)) return;
      event.preventDefault();
    };

    const onDrop = (event) => {
      if (!isOsFileDrag(event)) return;
      // Stop the browser from opening the dropped file; only then decide whether
      // this panel should consume it for upload.
      event.preventDefault();

      const file = event.dataTransfer?.files?.[0];
      // In-app plan drags can report odd type lists in some browsers but never
      // attach a File — bail so folder drop handlers keep working.
      if (!file) return;

      const {
        state: currentState,
        guideStep: currentGuideStep,
        handleFileSelect: onFileSelect,
        handleErrorFileSelect: onErrorFileSelect,
      } = windowDropRef.current;

      const accepting =
        currentGuideStep != null ||
        currentState === "idle" ||
        currentState === "error";
      if (!accepting) return;

      if (currentGuideStep != null || currentState === "error") {
        onErrorFileSelect(file);
      } else {
        onFileSelect(file);
      }
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  /**
   * External AI completion creates/updates the user's saved layout on the backend.
   * Invalidate so `/main` library (GET /savedLayout/) refetches instead of showing a stale list.
   */
  useEffect(() => {
    if (state === "done" && galleryUrl) {
      dispatch(apiRoomNate.util.invalidateTags(["savedLayouts"]));
      const resolvedId = uniqueId || galleryUrl.split("/").pop() || "";
      const onceKey = `${galleryUrl}|${resolvedId}`;
      if (uploadCompleteOnceKey.current !== onceKey) {
        uploadCompleteOnceKey.current = onceKey;
        onUploadComplete?.(resolvedId);
      }
    }
    if (state === "idle" || state === "uploading" || state === "processing") {
      uploadCompleteOnceKey.current = "";
    }
  }, [state, galleryUrl, dispatch, uniqueId, onUploadComplete]);

  /**
   * Fire postMessage to parent on success in integration mode
   */
  useEffect(() => {
    if (state === "done" && !!galleryUrl) {
      const completedGalleryUrl = targetGalleryUrl || galleryUrl;
      const resolvedId = uniqueId || completedGalleryUrl.split("/").pop() || "";
      if (window.parent !== window) {
        window.parent.postMessage(
          {
            type: "mf:complete",
            galleryUrl: completedGalleryUrl,
            uniqueId: resolvedId,
            ref: ref || "",
          },
          "*"
        );
      }
    }
  }, [state, isIntegrationMode, galleryUrl, targetGalleryUrl, uniqueId, ref]);

  const handleOpenGallery = () => {
    if (targetGalleryUrl) {
      trackLayoutOpened(targetGalleryUrl);

      if (isIntegrationMode) {
        handleExit();
      } else {
        const newWindow = window.open(
          targetGalleryUrl + "?redirect-to-editor=",
          "_blank"
        );

        if (!newWindow) return;

        const handleMessage = (event) => {
          if (event.source !== newWindow) return;

          const isOpenedSignal =
            event.data?.type === "mf:floor_plan_opened" ||
            (event.data?.type === "floor_plan_event" &&
              event.data?.payload?.event === "webview_opened");

          if (isOpenedSignal) {
            newWindow.postMessage(
              {
                type: "mf:set_ref",
                payload: { ref: ref || targetGalleryUrl },
              },
              event.origin
            );
            window.removeEventListener("message", handleMessage);
          }

          if (window.parent !== window) {
            window.parent.postMessage(
              {
                type: event.data?.type,
                payload: event.data?.event,
              },
              "*"
            );
          }
        };
        window.addEventListener("message", handleMessage);
      }
    } else {
      console.error("No gallery URL available");
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (state === "uploading" || state === "processing") {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [state]);

  const renderContent = () => {
    if (guideStep) {
      return (
        <RequirementsCard
          step={guideStep}
          onStepChange={setGuideStep}
          onUploadAnother={handleGuideUploadAnother}
          onDismiss={() => setGuideStep(null)}
        />
      );
    }

    switch (state) {
      case "idle":
        if (isUnsupportedValidation) {
          return (
            <ErrorCard
              errorType="INVALID_TYPE"
              onFileSelect={handleErrorFileSelect}
              onSeeWhatWorks={openGuide}
            />
          );
        }
        return (
          <UploadCard
            onFileSelect={handleFileSelect}
            validationError={validationError}
            onSeeWhatWorks={openGuide}
          />
        );

      case "uploading":
        return (
          <UploadingCard
            previewUrl={previewUrl}
            fileName={selectedFile?.name}
            fileSize={selectedFile ? formatFileSize(selectedFile.size) : null}
            onCancel={handleCancel}
            progress={uploadProgress}
          />
        );

      case "processing":
        return (
          <ProcessingCard
            statusMessage={
              processingStatus === "queued"
                ? "Your floor plan is in the queue..."
                : processingStatus === "processing"
                ? "Analyzing floor plan..."
                : "Processing your floor plan..."
            }
            elapsedTime={elapsedTime}
            onCancel={handleCancel}
          />
        );

      case "done":
        return (
          <DoneCard onOpenGallery={handleOpenGallery} onTryAgain={handleTryAgain} />
        );

      case "error":
        return (
          <ErrorCard
            errorType={uploadError?.errorType}
            errorMessage={uploadError?.message}
            onFileSelect={handleErrorFileSelect}
            onSeeWhatWorks={openGuide}
          />
        );

      default:
        return null;
    }
  };

  const cardSlot = (
    <Box
      sx={{
        height: CONTENT_BLOCK_HEIGHT,
        minHeight: CONTENT_BLOCK_HEIGHT,
        maxHeight: CONTENT_BLOCK_HEIGHT,
        width: "100%",
        maxWidth: CARD_MAX_WIDTH,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {renderContent()}
      <input
        ref={guideUploadInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={handleGuideUploadChange}
        style={{ display: "none" }}
        aria-hidden="true"
      />
    </Box>
  );

  if (layoutMode === "embedded") {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        {cardSlot}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "transparent",
        background: "transparent",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
        {cardSlot}
      </Box>
    </Box>
  );
};

UploadFlowPanel.propTypes = {
  layoutMode: PropTypes.oneOf(["fullscreen", "embedded"]),
  onUploadComplete: PropTypes.func,
};

UploadFlowPanel.defaultProps = {
  layoutMode: "fullscreen",
  onUploadComplete: undefined,
};

export default UploadFlowPanel;
